// src/pages/PortalSaaS/modules/Configuracoes.js
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';

const Configuracoes = () => {
    const { workspaceData } = useOutletContext();
    const navigate = useNavigate();
    const receita = workspaceData.dados_receita || {};

    // Estados para a Gestão Local
    const [equipeLocal, setEquipeLocal] = useState([]);
    const [isLoadingEquipe, setIsLoadingEquipe] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);

    // --- A TRAVA DE SEGURANÇA BLINDADA ---
    // Cobre todas as formas que o objeto pode ter chegado do servidor
    const isOwner = 
        workspaceData?.role === 'OWNER' || 
        workspaceData?.minhaRole === 'OWNER' || 
        workspaceData?.permissao_global === true;

    // --- BUSCA A EQUIPE ESPECÍFICA DESTA UNIDADE ---
    useEffect(() => {
        const fetchEquipeLocal = async () => {
            if (!isOwner || !workspaceData?.cnpj_completo) return;
            
            try {
                const q = query(
                    collection(db, 'usuarios_workspace'),
                    where('workspaceId', '==', workspaceData.cnpj_completo),
                    where('status', '==', 'ACTIVE')
                );
                
                const snapshot = await getDocs(q);
                const membros = snapshot.docs.map(doc => doc.data());
                setEquipeLocal(membros);
            } catch (error) {
                console.error("Erro ao buscar equipe local:", error);
            } finally {
                setIsLoadingEquipe(false);
            }
        };

        fetchEquipeLocal();
    }, [workspaceData?.cnpj_completo, isOwner]);

    // Função local para chamar a revogação sem precisar abrir a modal grande
    const handleRevokeLocal = async (targetUser) => {
        if (targetUser.role === 'OWNER') return alert("Impossível revogar o Sócio-Proprietário.");
        
        if (window.confirm(`Deseja revogar o acesso de ${targetUser.email} desta unidade? A fatura será reduzida em R$ 89,00.`)) {
            try {
                const revokeFn = httpsCallable(functions, 'revokeUserAccess');
                await revokeFn({
                    targetUid: targetUser.uid,
                    targetWorkspaceId: targetUser.workspaceId,
                    cnpjRaizOwner: targetUser.cnpj_raiz
                });
                alert("Acesso revogado com sucesso!");
                // Remove o usuário da lista na tela instantaneamente
                setEquipeLocal(prev => prev.filter(u => u.uid !== targetUser.uid));
            } catch (e) {
                alert("Falha ao revogar: " + e.message);
            }
        }
    };

    const handleDeleteFilial = async () => {
        const confirmacao = window.prompt(`ZONA DE PERIGO: Para confirmar o encerramento desta filial, digite o CNPJ exatamente como mostrado: ${workspaceData.cnpj_completo}`);
        
        if (confirmacao === workspaceData.cnpj_completo) {
            setIsDeleting(true);
            try {
                const deleteFilialFn = httpsCallable(functions, 'deleteFilialWorkspace');
                await deleteFilialFn({
                    filialId: workspaceData.cnpj_completo,
                    cnpjRaizOwner: workspaceData.cnpj_raiz
                });
                
                alert("✅ Filial encerrada com sucesso! A fatura da Matriz foi reajustada no Asaas.");
                // Redireciona o Owner de volta para a Matriz
                navigate(`/${workspaceData.slug.split('-')[0]}/dashboard`); 
                window.location.reload(); // Força o recarregamento dos claims e permissões
            } catch (e) {
                alert("Erro ao excluir filial: " + e.message);
                setIsDeleting(false);
            }
        } else if (confirmacao !== null) {
            alert("CNPJ incorreto. Ação de exclusão cancelada por segurança.");
        }
    };

    if (!isOwner) {
        return (
            <div className="pts-fade-in" style={{ textAlign: 'center', padding: '100px 20px' }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🛡️</div>
                <h2 style={{ color: '#0f172a', marginBottom: '10px' }}>Acesso Restrito à Diretoria</h2>
                <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto 30px auto' }}>
                    As configurações de governança, dados fiscais e faturamento são exclusivas para Sócios-Proprietários da organização.
                </p>
                <button className="pts-btn-primary" onClick={() => navigate(-1)}>
                    VOLTAR PARA O PAINEL
                </button>
            </div>
        );
    }

    // Filtra quantos funcionários "NÃO-SÓCIOS" existem para a trava de segurança
    const funcionariosAtivos = equipeLocal.filter(u => u.role !== 'OWNER');

    return (
        <div className="pts-fade-in" style={{ maxWidth: '900px', paddingBottom: '50px' }}>
            <h1 style={{ marginBottom: '30px' }}>⚙️ Configurações da Unidade</h1>

            {/* BLOCO 1: DADOS GERAIS */}
            <div className="pts-setup-card config" style={{ marginBottom: '25px' }}>
                <h3>🏢 Cartão CNPJ (Dados Oficiais)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                    <div>
                        <label className="pts-label">Razão Social</label>
                        <input className="pts-dynamic-input" value={receita.razao_social} disabled />
                    </div>
                    <div>
                        <label className="pts-label">CNPJ Unidade</label>
                        <input className="pts-dynamic-input" value={workspaceData.cnpj_completo} disabled />
                    </div>
                </div>
            </div>

            {/* BLOCO 2: FATURAMENTO */}
            <div className="pts-setup-card config" style={{ marginBottom: '25px' }}>
                <h3>💳 Governança e Tributação</h3>
                <div className="pts-billing-info">
                    <div>
                        <h4>Regime Fiscal: {receita.regime_fiscal || "Não Informado"}</h4>
                        <p>Plano Corporativo Ativo via Asaas ({workspaceData.tipo_unidade})</p>
                    </div>
                    {workspaceData.tipo_unidade === 'MATRIZ' && (
                        <button className="pts-btn-outline" onClick={() => alert("Redirecionando para extrato do Asaas...")}>
                            Ver Notas Fiscais
                        </button>
                    )}
                </div>
            </div>

            {/* BLOCO 3: GESTÃO DA EQUIPE LOCAL (MICRO-GOVERNANÇA) */}
            <div className="pts-setup-card config" style={{ marginBottom: '25px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ margin: 0, color: '#0f172a' }}>👥 Equipe Alocada Nesta Unidade</h3>
                    <span style={{ fontSize: '0.8rem', background: '#e2e8f0', padding: '4px 10px', borderRadius: '50px', fontWeight: 'bold' }}>
                        {isLoadingEquipe ? 'Carregando...' : `${equipeLocal.length} Membro(s)`}
                    </span>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {isLoadingEquipe ? (
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Buscando registros...</p>
                    ) : equipeLocal.length === 0 ? (
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Nenhum colaborador encontrado.</p>
                    ) : (
                        equipeLocal.map(user => (
                            <div key={user.uid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '12px 15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div>
                                    <strong style={{ display: 'block', fontSize: '0.9rem', color: '#0f172a' }}>{user.email}</strong>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: user.role === 'OWNER' ? '#0369a1' : '#10b981' }}>
                                        {user.role}
                                    </span>
                                </div>
                                {user.role !== 'OWNER' && (
                                    <button 
                                        onClick={() => handleRevokeLocal(user)}
                                        style={{ background: 'none', border: '1px solid #fca5a5', color: '#ef4444', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold' }}
                                        onMouseOver={e => { e.currentTarget.style.background = '#fef2f2'; }}
                                        onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
                                    >
                                        Remover
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* BLOCO 4: ZONA DE PERIGO (APENAS PARA FILIAIS) */}
            {workspaceData.tipo_unidade === 'FILIAL' && (
                <div className="pts-setup-card config" style={{ border: '1px solid #fecaca', background: '#fff5f5' }}>
                    <h3 style={{ color: '#ef4444', margin: '0 0 10px 0' }}>⚠️ Zona de Perigo</h3>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ maxWidth: '65%' }}>
                            <strong style={{ display: 'block', color: '#991b1b', fontSize: '1rem' }}>Encerrar Operações da Filial</strong>
                            <p style={{ fontSize: '0.8rem', color: '#b91c1c', margin: '5px 0 0 0', lineHeight: '1.4' }}>
                                Esta ação arquivará a unidade e reduzirá sua fatura no Asaas em R$ 499,00/mês. 
                                Os laudos e históricos gerados por esta unidade serão mantidos em modo de leitura para compliance.
                            </p>
                        </div>
                        
                        <div style={{ textAlign: 'right' }}>
                            <button 
                                disabled={funcionariosAtivos.length > 0 || isDeleting}
                                onClick={handleDeleteFilial}
                                style={{ 
                                    background: funcionariosAtivos.length > 0 ? '#fca5a5' : '#ef4444', 
                                    color: 'white', padding: '12px 20px', border: 'none', borderRadius: '8px', 
                                    cursor: funcionariosAtivos.length > 0 ? 'not-allowed' : 'pointer', 
                                    fontWeight: 'bold', transition: '0.2s' 
                                }}
                            >
                                {isDeleting ? 'ENCERRANDO...' : 'EXCLUIR FILIAL'}
                            </button>
                            
                            {funcionariosAtivos.length > 0 && (
                                <p style={{ fontSize: '0.65rem', color: '#991b1b', marginTop: '8px', maxWidth: '200px', fontWeight: 'bold' }}>
                                    Remova os {funcionariosAtivos.length} colaborador(es) acima antes de encerrar.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Configuracoes;